import "./style.css";
import "./VoiceOptionsDisplay.css";
import React, {
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import {
  Anchor,
  Badge,
  Button,
  Col,
  Drawer,
  Input,
  Menu,
  Row,
  App,
  Empty,
  Tag,
  Modal,
} from "antd";
import {
  CloseOutlined,
  SearchOutlined,
  ShoppingCartOutlined,
  UnorderedListOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { ConversationProvider } from "@elevenlabs/react";
import CurrencyFormatter from "../../../components/CurrencyFormatter";
import { useNavigate } from "react-router-dom";
import { AppContext } from "../../../context/AppContextProvider";
import { placeQRproductsOrder } from "../../../network/network";
import { useVoiceAgent } from "./useVoiceAgent";
import FloatingVoiceButton from "./FloatingVoiceButton";
import OrderSuccessModal from "./OrderSuccessModal";
import {
  QR_LANGUAGES,
  applyQrLanguage,
  qrLangShort,
  readStoredQrLocale,
} from "./qrLanguage";
import { shortOrderNumber } from "./qrOrderNumber";
import {
  clearCartIfMenuChanged,
  clearQrCart,
  getQrMenuProductIds,
  saveQrCartItems,
  saveQrMenuProductIds,
} from "./qrCartStorage";
import { handleApiError, showAppError } from "../../../utils/errorHandler";

const { Search } = Input;

const BADGE_DURATION_MS = 2000;

const readData = (response) => response?.data || response?.response?.data;

// Accept options as an array, a comma-separated string, or undefined (not asked yet).
const normalizeOptions = (value) => {
  if (Array.isArray(value)) {
    return value;
  }

  if (typeof value === "string") {
    return value.trim()
      ? value.split(",").map((option) => option.trim()).filter(Boolean)
      : [];
  }

  return undefined;
};

const getVariantKey = (product) => {
  const selectedTagIds = product?.selectedTagIds || [];
  const sorted = [...selectedTagIds].map(String).sort().join(",");
  return `${product.id}_${sorted}`;
};

const levenshtein = (a, b) => {
  if (a.length === 0) return b.length;
  if (b.length === 0) return a.length;

  const matrix = [];
  for (let i = 0; i <= b.length; i++) {
    matrix[i] = [i];
  }
  for (let j = 0; j <= a.length; j++) {
    matrix[0][j] = j;
  }

  for (let i = 1; i <= b.length; i++) {
    for (let j = 1; j <= a.length; j++) {
      if (b.charAt(i - 1) === a.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1,
          Math.min(matrix[i][j - 1] + 1, matrix[i - 1][j] + 1),
        );
      }
    }
  }
  return matrix[b.length][a.length];
};

// Match a spoken item name against the menu. Exact matches win, then substring
// matches, and only then fuzzy (typo-tolerant) matches — so a fuzzy candidate
// can never override a real substring hit regardless of menu ordering.
const findClosestProduct = (searchTerm, products) => {
  const term = (searchTerm || "").toLowerCase().trim();
  if (!term) return null;

  const getThreshold = (len) => (len < 5 ? 1 : len < 9 ? 2 : 3);

  let substringMatch = null;
  let fuzzyMatch = null;
  let fuzzyDistance = Infinity;

  for (const product of products) {
    const nameEn = (product.name_en || "").toLowerCase();
    const nameAr = (product.name || "").toLowerCase();

    if (nameEn === term || nameAr === term) {
      return product;
    }

    if (!substringMatch && (nameEn.includes(term) || nameAr.includes(term))) {
      substringMatch = product;
      continue;
    }

    if (substringMatch) {
      continue;
    }

    const distEn = nameEn ? levenshtein(term, nameEn) : Infinity;
    const distAr = nameAr ? levenshtein(term, nameAr) : Infinity;
    const distance = Math.min(distEn, distAr);
    const matchedLength = distEn <= distAr ? nameEn.length : nameAr.length;
    const threshold = getThreshold(Math.max(term.length, matchedLength));

    if (distance <= threshold && distance < fuzzyDistance) {
      fuzzyDistance = distance;
      fuzzyMatch = product;
    }
  }

  return substringMatch || fuzzyMatch;
};

const QRmenuWithVoice = (props) => (
  <ConversationProvider>
    <QRmenuVoice {...props} />
  </ConversationProvider>
);

const QRmenuVoice = ({ categoriesProducts, companyDetails, onBackToOrderMode }) => {
  const { appConfig, setAppConfigWrapper } = useContext(AppContext);
  const { t, i18n } = useTranslation("qr");
  const navigate = useNavigate();
  const { message } = App.useApp();

  const isRtl = appConfig.direction === "rtl";

  const [productCategoriesList, setProductCategoriesList] = useState([]);
  const [productsList, setProductsList] = useState([]);
  const [activeCategory, setActiveCategory] = useState(0);
  const [search, setSearch] = useState("");
  const [cart, setCart] = useState([]);
  const [totalQuantity, setTotalQuantity] = useState(0);

  const [searchVisible, setSearchVisible] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);

  const [orderSuccessModal, setOrderSuccessModal] = useState(false);
  const [lastOrderSummary, setLastOrderSummary] = useState(null);

  const [showOptionsDisplay, setShowOptionsDisplay] = useState(false);
  const [currentOptionsProduct, setCurrentOptionsProduct] = useState(null);

  const [transientBadge, setTransientBadge] = useState(null);

  const cartRef = useRef(cart);
  const orderPlacedRef = useRef(false);
  const badgeTimeoutRef = useRef(null);

  useEffect(() => {
    cartRef.current = cart;
  }, [cart]);

  // ---------- Cart helpers ----------

  const recalcTotalQuantity = useCallback((items) => {
    const total = items.reduce((sum, item) => sum + (item.quantity || 0), 0);
    setTotalQuantity(total);
  }, []);

  const saveCart = useCallback(
    (items) => {
      setCart(items);
      recalcTotalQuantity(items);
      if (typeof window !== "undefined") {
        saveQrCartItems(items);
      }
    },
    [recalcTotalQuantity],
  );

  const flashBadge = useCallback((badge) => {
    setTransientBadge(badge);
    if (badgeTimeoutRef.current) {
      clearTimeout(badgeTimeoutRef.current);
    }
    badgeTimeoutRef.current = setTimeout(
      () => setTransientBadge(null),
      BADGE_DURATION_MS,
    );
  }, []);

  useEffect(() => {
    return () => {
      if (badgeTimeoutRef.current) {
        clearTimeout(badgeTimeoutRef.current);
      }
    };
  }, []);

  const productName = useCallback(
    (item) => (isRtl ? item.name || item.name_en : item.name_en || item.name),
    [isRtl],
  );

  // ---------- Voice client tools ----------

  const voiceAddToCart = useCallback(
    ({ itemName, quantity, selectedOptions }) => {
      const requestedQty = Number(quantity) > 0 ? Number(quantity) : 1;
      const options = normalizeOptions(selectedOptions);
      const product = findClosestProduct(itemName, productsList);

      if (!product) {
        return t("voice_product_not_found", { product: itemName });
      }

      if (product.is_stockable && product.stock <= 0) {
        return t("voice_product_out_of_stock", {
          product: productName(product),
        });
      }

      // Ask for options once (only when the agent hasn't supplied them yet).
      if (product.tags && product.tags.length > 0 && options === undefined) {
        const optionNames = product.tags
          .map((tag) => tag.name || tag.name_ar)
          .join(", ");

        setCurrentOptionsProduct(product);
        setShowOptionsDisplay(true);

        return t("voice_options_prompt", {
          product: productName(product),
          options: optionNames,
        });
      }

      let selectedTagIds = [];
      if (options && options.length > 0 && product.tags) {
        selectedTagIds = product.tags
          .filter((tag) =>
            options.some(
              (opt) =>
                opt.toLowerCase().includes((tag.name || "").toLowerCase()) ||
                opt.toLowerCase().includes((tag.name_ar || "").toLowerCase()),
            ),
          )
          .map((tag) => tag.id);
      }

      const cartNew = [...cartRef.current];
      const variantKey = getVariantKey({ ...product, selectedTagIds });
      const indexFound = cartNew.findIndex((p) => p.cart_key === variantKey);

      if (indexFound === -1) {
        if (product.is_stockable && requestedQty > product.stock) {
          return t("voice_stock_limited", {
            available: product.stock,
            product: productName(product),
          });
        }
        cartNew.push({
          ...product,
          quantity: requestedQty,
          selectedTagIds,
          cart_key: variantKey,
        });
      } else {
        const item = cartNew[indexFound];
        if (item.is_stockable && item.quantity + requestedQty > item.stock) {
          return t("voice_stock_limited", {
            available: item.stock,
            product: productName(item),
          });
        }
        cartNew[indexFound] = {
          ...item,
          quantity: item.quantity + requestedQty,
        };
      }

      saveCart(cartNew);
      setShowOptionsDisplay(false);
      setCurrentOptionsProduct(null);
      flashBadge("item-added");

      const optionsText =
        selectedTagIds.length > 0
          ? t("voice_with_options", {
              options: product.tags
                .filter((tag) => selectedTagIds.includes(tag.id))
                .map((tag) =>
                  isRtl ? tag.name_ar || tag.name : tag.name_en || tag.name,
                )
                .join(", "),
            })
          : "";

      return t("voice_added_quantity_to_cart", {
        quantity: requestedQty,
        product: productName(product),
        options: optionsText,
      });
    },
    [productsList, productName, saveCart, flashBadge, isRtl, t],
  );

  const voiceRemoveFromCart = useCallback(
    ({ itemName, quantity }) => {
      const product = findClosestProduct(itemName, productsList);

      if (!product) {
        return t("voice_item_not_in_cart", { product: itemName });
      }

      const cartNew = [...cartRef.current];
      const variantKey = getVariantKey(product);
      let indexFound = cartNew.findIndex((p) => p.cart_key === variantKey);

      if (indexFound === -1) {
        indexFound = cartNew.findIndex((p) => p.id === product.id);
      }

      if (indexFound === -1) {
        return t("voice_item_not_in_cart", { product: itemName });
      }

      const item = cartNew[indexFound];
      const removeQty = Number(quantity) > 0 ? Number(quantity) : item.quantity;
      const newQty = item.quantity - removeQty;

      if (newQty <= 0) {
        cartNew.splice(indexFound, 1);
      } else {
        cartNew[indexFound] = { ...item, quantity: newQty };
      }

      saveCart(cartNew);
      flashBadge("item-removed");

      return t("voice_removed_quantity", {
        quantity: removeQty,
        product: productName(item),
      });
    },
    [productsList, productName, saveCart, flashBadge, t],
  );

  const voiceGetCartContents = useCallback(() => {
    const currentCart = cartRef.current;

    if (currentCart.length === 0) {
      return t("basket_empty");
    }

    const itemsList = currentCart.map(
      (item) => `${item.quantity} ${productName(item)}`,
    );

    return t("voice_cart_contents", { items: itemsList.join(", ") });
  }, [productName, t]);

  const buildOrderSummary = useCallback(
    (serverData, cartItems) => ({
      orderId: shortOrderNumber(serverData?.order_id) || "N/A",
      items: cartItems.map((item) => ({
        name: productName(item),
        quantity: item.quantity,
        price: item.price,
        total: item.price * item.quantity,
        tags: (item.selectedTagIds || [])
          .map((id) => {
            const tag = (item.tags || []).find((tg) => tg.id === id);
            if (!tag) return null;
            return isRtl ? tag.name_ar || tag.name : tag.name_en || tag.name;
          })
          .filter(Boolean),
      })),
      totalAmount:
        serverData?.amount_charged ||
        cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0),
    }),
    [productName, isRtl],
  );

  const voiceSubmitOrder = useCallback(async () => {
    const currentCart = cartRef.current;

    if (currentCart.length === 0) {
      return t("voice_cart_empty_submit");
    }

    try {
      const transactionObj = {
        src: localStorage.getItem("src"),
        table: localStorage.getItem("table"),
        is_drive_thru: localStorage.getItem("is_drive_thru"),
        items: JSON.stringify(
          currentCart.map((item) => ({
            id: item.id,
            qty: item.quantity,
            price: item.price,
            tags: item.selectedTagIds?.length ? item.selectedTagIds : [],
          })),
        ),
      };

      const response = await placeQRproductsOrder(transactionObj);
      const data = readData(response);

      if (data?.success === true) {
        const orderSummary = buildOrderSummary(data.data, currentCart);

        setLastOrderSummary(orderSummary);
        saveCart([]);
        flashBadge("order-placed");

        // Deterministic confirmation — no fragile transcript matching or timers.
        if (!orderPlacedRef.current) {
          orderPlacedRef.current = true;
          setOrderSuccessModal(true);
        }

        return t("voice_order_success", { orderId: orderSummary.orderId });
      }

      handleApiError(response, t);
      return data?.message || t("voice_failed_to_place_order");
    } catch (error) {
      handleApiError(error, t);
      return t("voice_order_error");
    }
  }, [buildOrderSummary, saveCart, flashBadge, t]);

  // Optional deterministic signal the agent can call as its final step.
  const voiceConfirmOrderComplete = useCallback(() => {
    if (!orderPlacedRef.current) {
      return "";
    }
    setOrderSuccessModal(true);
    return "";
  }, []);

  const voiceTools = useMemo(
    () => ({
      addToCart: voiceAddToCart,
      removeFromCart: voiceRemoveFromCart,
      getCartContents: voiceGetCartContents,
      submitOrder: voiceSubmitOrder,
      confirmOrderComplete: voiceConfirmOrderComplete,
    }),
    [
      voiceAddToCart,
      voiceRemoveFromCart,
      voiceGetCartContents,
      voiceSubmitOrder,
      voiceConfirmOrderComplete,
    ],
  );

  const getSessionConfig = useCallback(() => {
    const inStock = productsList.filter(
      (p) => !p.is_stockable || (p.stock && p.stock > 0),
    );

    const availableProducts = inStock
      .flatMap((p) => {
        const names = [];
        if (p.name_en) names.push(p.name_en);
        if (p.name && p.name !== p.name_en) names.push(p.name);
        return names;
      })
      .join(", ");

    const productCatalog = inStock.map((p) => {
      const product = { en: p.name_en, ar: p.name, price: p.price };
      if (p.calories_info) {
        product.calories = p.calories_info;
      }
      if (p.tags && p.tags.length > 0) {
        product.options = p.tags
          .map((tag) => `${tag.name_en} (${tag.name_ar || tag.name})`)
          .join(", ");
      }
      return product;
    });

    return {
      src: localStorage.getItem("src"),
      table: localStorage.getItem("table"),
      // Starting language from the QR language selection — the agent's language
      // detection still switches if the customer speaks another language.
      language: readStoredQrLocale() || "en",
      dynamicVariables: {
        available_products: availableProducts,
        product_catalog: JSON.stringify(productCatalog),
        currency: t("currency_riyals"),
      },
    };
  }, [productsList, t]);

  const handleVoiceError = useCallback(
    (error) => {
      if (error?.response?.data || error?.data) {
        handleApiError(error, t);
        return;
      }

      showAppError(t("voice_connection_error"));
    },
    [t],
  );

  const handleVoiceAutoEnd = useCallback(
    (reason) => {
      const text =
        reason === "max_duration"
          ? t("voice_session_time_limit")
          : t("voice_session_inactive");
      message.info(text);
    },
    [message, t],
  );

  const voice = useVoiceAgent({
    enabled: productsList.length > 0,
    getSessionConfig,
    tools: voiceTools,
    onError: handleVoiceError,
    onAutoEnd: handleVoiceAutoEnd,
  });

  // ---------- Data initialization ----------

  useEffect(() => {
    const categories = [{ id: 0, name: t("all"), name_ar: t("all") }];
    let products = [];

    categoriesProducts.forEach((category) => {
      if (category.products.length > 0) {
        categories.push({
          id: category.id,
          name: category.name,
          name_ar: category.name_ar,
        });
        products = [...products, ...category.products];
      }
    });

    const menuProductIds = getQrMenuProductIds(categoriesProducts);
    const cartWasCleared = clearCartIfMenuChanged(menuProductIds);

    saveQrMenuProductIds(menuProductIds);
    setProductCategoriesList(categories);
    setProductsList(products);

    if (cartWasCleared) {
      setCart([]);
      recalcTotalQuantity([]);
    }
  }, [t, categoriesProducts, recalcTotalQuantity]);

  useEffect(() => {
    const storedCart = localStorage.getItem("cart_items");
    if (!storedCart) {
      return;
    }

    try {
      const cartItems = JSON.parse(storedCart);
      if (!Array.isArray(cartItems) || cartItems.length === 0) {
        return;
      }

      const menuProductIds = getQrMenuProductIds(categoriesProducts);
      const cartWasCleared = clearCartIfMenuChanged(menuProductIds);

      if (cartWasCleared) {
        setCart([]);
        recalcTotalQuantity([]);
        return;
      }

      const normalizedCart = cartItems.map((item) => {
        const selectedTagIds = item.selectedTagIds || [];
        const cart_key =
          item.cart_key || getVariantKey({ ...item, selectedTagIds });
        return { ...item, selectedTagIds, cart_key };
      });

      setCart(normalizedCart);
      recalcTotalQuantity(normalizedCart);
    } catch (error) {
      clearQrCart();
    }
  }, [categoriesProducts, recalcTotalQuantity]);

  // ---------- UI handlers ----------

  const handleBackToOrderMode = () => {
    voice.stop();
    if (onBackToOrderMode) {
      onBackToOrderMode();
    }
  };

  const handleFloatingButtonClick = () => {
    // Pause = fully end the session. ElevenLabs only mutes the mic (input); the
    // agent stays live, keeps talking, and nudges "are you still there?".
    // Ending the session truly stops it and saves credits; tapping again starts
    // a fresh session (the cart persists).
    if (voice.isActive) {
      voice.stop();
      return;
    }
    if (!voice.isInitializing) {
      voice.start();
    }
  };

  const handleOrderSuccessClose = () => {
    setOrderSuccessModal(false);
    orderPlacedRef.current = false;
    voice.stop();
  };

  const onChangeSearchValue = (e) => setSearch(e.target.value);

  const filteredProducts = useMemo(() => {
    if (!search) {
      return productsList;
    }
    const term = search.toLowerCase();
    return productsList.filter(
      (product) =>
        (product.name_en || "").toLowerCase().includes(term) ||
        (product.name || "").toLowerCase().includes(term),
    );
  }, [productsList, search]);

  const buttonStatus =
    transientBadge ||
    (voice.status === "connecting"
      ? "listening"
      : voice.status === "error"
        ? "idle"
        : voice.status);

  const productCards = useMemo(
    () =>
      filteredProducts.map((product) => {
        const inActiveCategory =
          Number(activeCategory) === 0 ||
          Number(activeCategory) === product.category_id;

        if (!inActiveCategory) return null;

        const outOfStock = product.is_stockable && Number(product.stock) === 0;
        const canClick = !product.is_stockable || Number(product.stock) > 0;

        return (
          <Col xs={24} sm={12} md={12} lg={8} xl={6} xxl={6} key={product.id}>
            <div style={{ padding: "10px" }}>
              <div
                className="qr-product-card"
                style={{
                  position: "relative",
                  borderRadius: "16px",
                  padding: "10px",
                  backgroundColor: "var(--color-white)",
                  boxShadow: "0 10px 24px var(--color-shadow-soft)",
                  border: "1px solid var(--color-bg)",
                  display: "flex",
                  columnGap: "10px",
                  transition: "transform 0.15s ease, box-shadow 0.15s ease",
                  cursor: canClick ? "pointer" : "not-allowed",
                  opacity: canClick ? 1 : 0.7,
                }}
              >
                {outOfStock && (
                  <div
                    style={{ position: "absolute", top: 10, left: 10, zIndex: 2 }}
                  >
                    <Tag color="red">{t("out_of_stock")}</Tag>
                  </div>
                )}

                <img
                  alt={product.name_en}
                  src={product.image ? product.image : "/images/image-not-found.png"}
                  style={{
                    width: 96,
                    height: 96,
                    borderRadius: "12px",
                    border: "1px solid var(--color-border)",
                    objectFit: "cover",
                    flexShrink: 0,
                  }}
                />

                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    width: "100%",
                  }}
                >
                  <div>
                    <div
                      style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}
                    >
                      {isRtl ? product.name : product.name_en}
                    </div>
                    {companyDetails?.is_calories && product.calories_info && (
                      <div
                        style={{
                          fontSize: 12,
                          marginTop: 2,
                          color: "var(--color-text-muted)",
                          fontWeight: 500,
                        }}
                      >
                        {t("calories")}: {product.calories_info}
                      </div>
                    )}
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        width: "100%",
                        fontSize: 12,
                        lineHeight: 1,
                        fontWeight: 600,
                        marginTop: 8,
                      }}
                    >
                      <div>
                        <CurrencyFormatter value={product.price} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Col>
        );
      }),
    [filteredProducts, activeCategory, isRtl, companyDetails, t],
  );

  // ---------- Render ----------

  return (
    <div style={{ direction: appConfig.direction }}>
      {/* Header / Search */}
      {searchVisible ? (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            columnGap: "10px",
            boxShadow: "var(--color-shadow-soft) 5px 10px 80px 0px",
            padding: "10px",
          }}
        >
          <Button
            type="primary"
            onClick={() => setSearchVisible(false)}
            icon={<CloseOutlined />}
            className="app-square-40"
          />

          <Search
            onChange={onChangeSearchValue}
            allowClear
            placeholder={t("search_product_name_here")}
            size="large"
            enterButton
            style={{ width: "calc(100% - 50px)" }}
          />
        </div>
      ) : (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            columnGap: "10px",
            boxShadow: "var(--color-shadow-soft) 5px 10px 80px 0px",
            padding: "10px",
          }}
        >
          <div
            className="app-flex-align-center-gap-10"
          >
            <img
              alt={companyDetails.company_name}
              src={
                companyDetails.logo
                  ? companyDetails.logo
                  : "/images/image-not-found.png"
              }
              style={{
                width: "100%",
                maxWidth: 40,
                height: "auto",
                overflow: "hidden",
                borderRadius: "35rem",
              }}
            />
            <div>
              <div
                style={{
                  fontSize: 14,
                  fontWeight: 600,
                  textTransform: "capitalize",
                  maxWidth: 150,
                  lineHeight: 1,
                  marginTop: 3,
                }}
              >
                {companyDetails.company_name}
              </div>
            </div>
          </div>

          <div className="app-flex-gap-10">
            <Button
              type="primary"
              onClick={() => setSearchVisible(true)}
              icon={<SearchOutlined />}
              className="app-square-40"
            />
            <Button
              type="primary"
              onClick={() => setDrawerVisible(true)}
              className="app-square-40"
            >
              {qrLangShort(i18n.language)}
            </Button>
            {onBackToOrderMode && (
              <Button
                type="default"
                onClick={handleBackToOrderMode}
                style={{ height: 40 }}
              >
                {t("change_mode")}
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Body */}
      {productCategoriesList.length < 2 ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "calc(100vh - 110px)",
          }}
        >
          <Empty />
        </div>
      ) : (
        <>
          {/* Category Tabs (Anchor) */}
          <Anchor
            className="qr-anchor"
            direction="horizontal"
            style={{
              padding: "10px",
              backgroundColor: "var(--color-bg)",
              overflowX: "auto",
              whiteSpace: "nowrap",
            }}
            items={productCategoriesList.map((category) => {
              const isActive = Number(activeCategory) === category.id;
              return {
                key: category.id,
                href: `#category-${category.id}`,
                title: (
                  <div
                    style={{
                      background: isActive
                        ? "var(--color-primary)"
                        : "var(--color-white)",
                      color: isActive
                        ? "var(--color-white)"
                        : "var(--color-black)",
                      padding: "5px 15px",
                      borderRadius: "35rem",
                      cursor: "pointer",
                      display: "inline-block",
                    }}
                  >
                    {isRtl ? category.name_ar : category.name}
                  </div>
                ),
              };
            })}
            onClick={(e, link) => {
              e.preventDefault();
              const href = link?.href;
              if (!href) return;
              const id = Number(href.replace("#category-", ""));
              if (!Number.isNaN(id)) setActiveCategory(id);
            }}
          />

          {/* Product Grid */}
          <Row style={{ paddingBottom: 60 }}>{productCards}</Row>

          {/* Bottom Bar */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              columnGap: "10px",
              boxShadow: "1px -2px 4px 0 var(--color-shadow-soft)",
              padding: "10px",
              position: "fixed",
              bottom: 0,
              left: 0,
              right: 0,
              backgroundColor: "var(--color-white)",
              zIndex: 9,
            }}
          >
            {companyDetails?.orders?.length > 0 && (
              <Button
                type="primary"
                style={{
                  padding: "0 10px",
                  height: 40,
                  lineHeight: 1,
                  display: "flex",
                  alignItems: "center",
                  columnGap: "7px",
                }}
                onClick={() => navigate("/qr/history")}
              >
                <span>{t("your_order")}</span>
                <UnorderedListOutlined style={{ fontSize: 25, lineHeight: 0 }} />
              </Button>
            )}

            <div
              style={{
                fontSize: "11px",
                color: "var(--color-text-muted)",
                letterSpacing: "0.5px",
                textTransform: "uppercase",
              }}
            >
              {t("powered_by")}{" "}
              <a
                href="https://www.anypos.ai/"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: "var(--color-primary)",
                  textDecoration: "none",
                  fontWeight: 500,
                }}
              >
                AnyPOS
              </a>
            </div>

            <Button
              type="primary"
              style={{
                padding: "0 10px",
                height: 40,
                lineHeight: 1,
                display: "flex",
                alignItems: "center",
                columnGap: "7px",
              }}
              onClick={() => navigate("/qr/cart")}
            >
              <span>{t("cart")}</span>
              <Badge count={totalQuantity}>
                <ShoppingCartOutlined
                  style={{
                    fontSize: 25,
                    lineHeight: 0,
                    color: "var(--color-white)",
                  }}
                />
              </Badge>
            </Button>
          </div>
        </>
      )}

      {/* Language Drawer */}
      <Drawer
        title={t("select_language")}
        placement={isRtl ? "left" : "right"}
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
        styles={{ body: { padding: 0 } }}
        closable={false}
        extra={
          <Button
            type="text"
            danger
            icon={<CloseOutlined />}
            onClick={() => setDrawerVisible(false)}
          />
        }
      >
        <Menu
          selectedKeys={[readStoredQrLocale() || "en"]}
          items={QR_LANGUAGES.map((language) => ({
            key: language.code,
            label: language.label,
            onClick: () => {
              applyQrLanguage(language.code, setAppConfigWrapper);
              setDrawerVisible(false);
            },
          }))}
        />
      </Drawer>

      {/* Voice Options Modal */}
      {showOptionsDisplay &&
        currentOptionsProduct &&
        currentOptionsProduct.tags &&
        currentOptionsProduct.tags.length > 0 && (
          <Modal
            open={true}
            onCancel={() => {
              setShowOptionsDisplay(false);
              setCurrentOptionsProduct(null);
            }}
            footer={null}
            centered
            title={null}
            className="voice-options-modal"
            width={380}
          >
            <div className="voice-options-content">
              <h2 className="voice-options-title">
                {t("available_options")}
              </h2>
              <h3 className="voice-options-product">
                {isRtl
                  ? currentOptionsProduct.name
                  : currentOptionsProduct.name_en}
              </h3>
              <div className="voice-options-grid">
                {currentOptionsProduct.tags.map((tag) => (
                  <div key={tag.id} className="voice-option-card">
                    <span>{tag.name || tag.name_ar}</span>
                  </div>
                ))}
              </div>
              <p className="voice-options-instruction">
                {t("voice_options_instruction")}
              </p>
            </div>
          </Modal>
        )}

      {/* Order Success Modal */}
      <OrderSuccessModal
        visible={orderSuccessModal}
        onClose={handleOrderSuccessClose}
        orderSummary={lastOrderSummary}
      />

      {/* Floating Voice Button */}
      <FloatingVoiceButton
        onClick={handleFloatingButtonClick}
        isActive={voice.isActive}
        status={buttonStatus}
        isInitializing={voice.isInitializing}
      />
    </div>
  );
};

export default QRmenuWithVoice;
